import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Menu } from "primereact/menu";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { classNames as cx } from "primereact/utils";
import { Button } from "primereact/button";

import { If } from "../../../../If/If";
import { GroupItem, IGroup } from "./GroupItem/GroupItem";
import { useKeycloakContext } from "../../../../../keycloak";
import { CreateGroupForm } from "./CreateGroupForm/CreateGroupForm";
import { NewDirectMessage } from "./NewDirectMessage/NewDirectMessage";
import SocketContext from "../../../../../socket/SocketProvider/SocketProvider";
import { Messages } from "./Messages/Messages";
import {
  IGroupData,
  IMessage,
  IMessagesResponse,
  RoomTypes,
} from "../../../../../models/Chat";
import { Employee } from "../../../../../models/Employee";
import { useToast } from "../../../../../Providers/ToastProvider";

import classes from "./Chat.module.scss";

const sortGroupByDateCB = (a: any, b: any) => {
  const dateA = new Date(a.lastMessage?.createdAt || a.createdAt);
  const dateB = new Date(b.lastMessage?.createdAt || b.createdAt);
  if (dateA < dateB) {
    return 1;
  } else if (dateA > dateB) {
    return -1;
  } else {
    return 0;
  }
};

const modifyGroups = (groups: IGroupData[], subId: string): IGroup[] => {
  return groups
    .map(
      ({
        id,
        createdAt,
        createdBy,
        name,
        participants,
        lastMessage,
        type,
        hasSeen,
      }) => {
        const filteredParticipants = participants.filter((p) => p.id !== subId);

        const participantsNames = filteredParticipants
          ?.map((p) => p.firstName + " " + p.lastName)
          .join(", ");

        return {
          createdAt,
          createdBy,
          name,
          participantsNames,
          ...(lastMessage && {
            lastMessage: {
              text: lastMessage.text,
              creator: lastMessage.creator,
              createdAt: lastMessage.createdAt,
              type: lastMessage.type,
            },
          }),
          id,
          type,
          hasSeen,
          ...(type === RoomTypes.Direct && {
            role: filteredParticipants[0]?.role,
          }),
        };
      }
    )
    .sort(sortGroupByDateCB);
};

export const Chat = (): JSX.Element => {
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [showCreateGroupForm, setShowCreateGroup] = useState<boolean>(false);
  const [showNewDirectMessage, setShowNewDirectMessage] =
    useState<boolean>(false);
  const [showMessages, setShowMessages] = useState<boolean>(true);
  const [showTabs, setShowTabs] = useState<boolean>(true);
  const [newMessage, setNewMessage] = useState<IMessage[]>([]);
  const chatPopover = useRef(null);
  const isChatOpened = useRef<boolean>(false);

  // const [searchValue] = useState<string>('')

  const keycloakContext = useKeycloakContext();
  const frId = keycloakContext?.keycloak?.tokenParsed?.frId;
  const subId = keycloakContext?.keycloak?.tokenParsed?.sub;

  const socket = useContext(SocketContext);

  const { show } = useToast();

  useEffect(() => {
    if (!groups.length) {
      setShowMessages(false);
    } else {
      setShowMessages(true);
    }

    if (socket) {
      socket.on("userAllRooms", (rooms: { result: IGroupData[] }) => {
        setGroups((prev) => {
          const selectedId = prev.find((g) => g.isSelected)?.id;
          return modifyGroups(rooms.result, subId).map((g) => ({
            ...g,
            isSelected: g.id === selectedId,
          }));
        });
      });

      socket.on("roomCreated", (room: IGroupData) => {
        const foundIndex = groups.findIndex((g) => g.id === room.id);
        let modifiedGroup = modifyGroups([room], subId)[0];

        setGroups((prev) => {
          if (foundIndex === -1) {
            modifiedGroup.hasSeen = true;
            return [modifiedGroup, ...prev].map((g) => ({
              ...g,
              isSelected: g.id === modifiedGroup.id,
            }));
          }
          return prev.map((g) => ({ ...g, isSelected: g.id === room.id }));
        });
        resetToMain();
      });

      socket.on("disconnect", () => {
        console.log("disconnected"); // TODO: remove after test
      });

      socket.on("exception", (res: { message: string }) => {
        show({ severity: "error", detail: res.message });
      });

      socket.on("roomsRemoved", (res: { roomId: number }) => {
        setGroups((prev) => prev.filter((g) => g.id !== res.roomId));
      });

      socket.on("messageSent", (data: IMessagesResponse) => {
        const lastMessage = data.result[0];
        const selectedGroupId = groups.find((g) => g.isSelected === true)?.id;

        handleNewMessageReceive(lastMessage, selectedGroupId);
        if (lastMessage.roomId !== selectedGroupId) {
          return;
        }
        socket.emit("messageSeen", {
          roomId: selectedGroupId,
        });
        setNewMessage(data.result);
      });

      return () => {
        socket.off("roomCreated");
        socket.off("userAllRooms");
        socket.off("exception");
        socket.off("roomsRemoved");
        socket.off("messageSent");
      };
    }
  }, [socket, groups]);

  // const filteredConversationsList = useMemo(()=>{

  //   if (searchValue) {
  //     return groups.filter(g => {
  //       if (g.type === RoomTypes.Group) {
  //         return g.name.includes(searchValue)
  //       }
  //     })
  //   }

  //   return groups

  // },[searchValue,groups])

  const openChat = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    // setSelectedGroup(filteredConversationsList[0]);
    setGroups((prev) =>
      prev.map((g, i) => ({
        ...g,
        isSelected: i === 0,
        hasSeen: i === 0 ? true : g.hasSeen,
      }))
    );

    isChatOpened.current = !isChatOpened.current;

    //@ts-ignore
    chatPopover.current && chatPopover.current.toggle(e);
  };

  const newMessageButtonRef = useRef<any>(null);

  const newMessageItems = [
    {
      label: "Create New Message",
      command: () => {
        setNewMessage([]);
        setShowNewDirectMessage(true);
        setShowMessages(false);
        setShowTabs(false);
      },
    },
    {
      label: "Create New Group",
      command: () => {
        setNewMessage([]);
        setShowCreateGroup(true);
        setShowMessages(false);
        setShowTabs(false);
      },
    },
  ];

  const handleCreateGroup = (data: any) => {
    if (socket) {
      socket.emit("createRoom", {
        frId,
        name: data.groupName,
        participants: data.employeeIds,
        type: RoomTypes.Group,
      });
    }
  };

  const resetToMain = () => {
    setNewMessage([]);
    setShowCreateGroup(false);
    setShowTabs(true);
    setShowNewDirectMessage(false);
    setShowMessages(true);
  };

  const handleChatUserSelect = (user: Employee) => {
    if (socket) {
      socket.emit("createRoom", {
        frId,
        participants: [user.id],
        type: RoomTypes.Direct,
      });
    }
  };

  const handleGroupDelete = (id: number) => {
    if (socket) {
      socket.emit("removeRooms", {
        frId,
        roomIds: [id],
      });
      resetToMain();
    }
  };

  const handleGroupSelect = (id: number) => {
    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        isSelected: g.id === id,
        hasSeen: g.id === id ? true : g.hasSeen,
      }))
    );
  };

  const selectedGroup: IGroup | undefined = useMemo(() => {
    if (groups) {
      return groups.find((g) => g.isSelected);
    }

    return;
  }, [groups]);

  const hasNewMessage: boolean = useMemo(() => {
    return groups.some((g) => !g.hasSeen);
  }, [groups]);

  const handleNewMessageReceive = (
    message: IMessage,
    selectedGroupId?: number
  ) => {
    setGroups((prev) =>
      prev
        .map((g) => ({
          ...g,
          lastMessage:
            message.roomId === g.id
              ? {
                  creator: message.creator,
                  text: message.text,
                  createdAt: message.createdAt,
                  type: message.type,
                }
              : g.lastMessage,
          hasSeen:
            selectedGroupId !== message.roomId && message.roomId === g.id
              ? false
              : g.hasSeen,
        }))
        .sort(sortGroupByDateCB)
    );
  };

  return (
    <div className={classes.chatTrigger}>
      <i
        className={cx("pi pi-comment p-overlay-badge", classes.icon, {
          // [classes.active]: notificationsData?.total,
        })}
        onClick={openChat}
      >
        <If condition={hasNewMessage}>
          <Badge
            style={{ width: "12px", height: "12px", top: 8, right: 8 }}
            severity="danger"
          ></Badge>
        </If>
      </i>
      <OverlayPanel
        ref={chatPopover}
        id="overlay_panel"
        className={cx(classes.container, {
          [classes.isNewMessage]: showCreateGroupForm || showNewDirectMessage,
          [classes.noConversation]: !showMessages,
        })}
        onHide={() => {
          setGroups((prev) => prev.map((g) => ({ ...g, isSelected: false })));
          isChatOpened.current = false;
          resetToMain();
        }}
      >
        <div className={classes.content}>
          <div className={classes.chatPanel}>
            {showTabs ? (
              <>
                <div className={classes.title}>
                  <div>Messaging</div>
                  <Button
                    icon="pi pi-pencil"
                    aria-controls="new_message_menu"
                    className="p-button-text p-button-secondary"
                    onClick={(event) =>
                      newMessageButtonRef.current &&
                      newMessageButtonRef.current.toggle(event)
                    }
                  />
                  <Menu
                    model={newMessageItems}
                    popup
                    ref={newMessageButtonRef}
                    id="new_message_menu"
                  />
                </div>
                {/* <div className={classes.search}>
                <span className="p-input-icon-left">
                  <i className="pi pi-search" />
                  <InputText
                    onChange={(e) => setSearchValue(e.target.value)}
                    value={searchValue}
                    placeholder="Search"
                  />
                </span>
              </div> */}
                <div className={classes.groups}>
                  {groups?.length ? (
                    groups.map((group: IGroup) => (
                      <GroupItem
                        key={group.id}
                        {...group}
                        onGroupSelect={handleGroupSelect}
                        isSelected={group.isSelected}
                        onGroupDelete={handleGroupDelete}
                      />
                    ))
                  ) : (
                    <span className={classes.noConversations}>
                      No Conversations
                    </span>
                  )}
                </div>
              </>
            ) : null}
            <If condition={showCreateGroupForm}>
              <CreateGroupForm
                onBackClick={resetToMain}
                onSubmit={handleCreateGroup}
              />
            </If>
            {showNewDirectMessage ? (
              <NewDirectMessage
                onBackClick={resetToMain}
                onSelect={handleChatUserSelect}
              />
            ) : null}
          </div>
          {showMessages ? (
            <Messages
              groupName={selectedGroup?.name}
              groupId={selectedGroup?.id}
              participantsNames={selectedGroup?.participantsNames}
              type={selectedGroup?.type}
              role={selectedGroup?.role}
              newMessage={newMessage}
            />
          ) : null}
        </div>
      </OverlayPanel>
    </div>
  );
};
